import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, Target, Telescope, Users } from "lucide-react";

import { Container } from "@/platform/layout/container";
import { Badge } from "@/platform/ui/badge";
import { Button } from "@/platform/ui/button";
import { Blob, SoftStar } from "@/platform/ui/illustrations/blobs";
import { BrainSpot, TrophySpot } from "@/platform/ui/illustrations/spot";

export function AboutPage() {
  return (
    <div className="relative overflow-hidden bg-mesh">
      <Blob
        color="violet"
        className="pointer-events-none absolute -left-24 -top-16 w-96 opacity-30 blur-2xl animate-blob"
      />
      <Blob
        color="pink"
        className="pointer-events-none absolute -right-32 top-60 w-96 opacity-25 blur-2xl animate-blob"
      />
      <SoftStar
        color="yellow"
        className="pointer-events-none absolute right-12 top-24 w-12 opacity-60 animate-float"
      />

      <Container size="default" className="relative py-20 md:py-28">
        <header className="animate-fade-up max-w-3xl">
          <Badge variant="accentSolid" className="w-fit">
            <Sparkles className="size-3" aria-hidden />
            Sobre
          </Badge>
          <h1 className="mt-6 font-display text-[length:var(--text-display-lg)] font-semibold leading-[var(--text-display-lg--line-height)] tracking-[var(--text-display-lg--letter-spacing)] text-balance">
            Um laboratório de inteligência educacional para{" "}
            <span className="text-gradient-accent">vestibulares específicos.</span>
          </h1>
          <p className="mt-5 text-[length:var(--text-body-lg)] text-[color:var(--color-muted-foreground)] leading-[var(--text-body-lg--line-height)] text-pretty">
            Neuronix Study é uma plataforma de alto desempenho que combina engenharia reversa
            de banca, taxonomia detalhada, simulados calibrados e correção por IA com rigor
            acadêmico. Cada vestibular tem seu próprio produto: PsiEinstein PatternLab (Einstein
            · Vunesp · Psicologia) e FuvestPro USP (Fuvest · Engenharia de Produção).
          </p>
        </header>

        {/* Pilares */}
        <div className="mt-16 grid gap-5 md:grid-cols-3">
          <PilarCard
            tone="violet"
            spot={<BrainSpot className="size-12" />}
            label="Olhar honesto"
            title="A prova como ela é"
            body="100 pontos, 5h, fase única. 50 objetivas, 5 discursivas, 1 redação Vunesp. Nota padronizada média 500, DP 100."
          />
          <PilarCard
            tone="pink"
            spot={<TrophySpot className="size-12" />}
            label="Time interdisciplinar"
            title="Psicometria + pedagogia"
            body="Cognição, design, engenharia. Cada decisão nasce do corpus oficial e do conteúdo programático do curso de Psicologia."
            icon={Users}
          />
          <PilarCard
            tone="mint"
            spot={
              <span className="grid size-12 place-items-center rounded-full bg-[color:var(--color-mint)] text-white">
                <Target className="size-5" aria-hidden />
              </span>
            }
            label="Sem promessa fake"
            title="Reduzir risco, não vender ilusão"
            body="Não prometemos aprovação garantida. Reduzimos drasticamente o risco de reprovação por meio de diagnóstico, treino adaptativo e simulação."
          />
        </div>

        {/* Corpo editorial */}
        <div className="mt-16 grid gap-12 md:grid-cols-[1fr_2fr]">
          <aside className="space-y-3">
            <Badge variant="pink">Tese central</Badge>
            <p className="font-display text-[length:var(--text-h3)] font-semibold leading-[var(--text-h3--line-height)] tracking-[var(--text-h3--letter-spacing)] text-balance">
              Aprovação é vantagem <span className="text-gradient-accent">relativa</span>, não
              absoluta.
            </p>
          </aside>
          <article className="space-y-5 text-[length:var(--text-body)] text-[color:var(--color-foreground)] leading-[var(--text-body--line-height)]">
            <p>
              Olhamos para a prova como ela é: 100 pontos, 5 horas, fase única. Reconhecemos que
              metade da seleção está fora da múltipla escolha — em redação e discursivas — e que
              quem treina só o objetivo está jogando 50% da seleção fora.
            </p>
            <p>
              Construímos um time interdisciplinar — psicometria, pedagogia, ciência cognitiva,
              design e engenharia — para transformar essa anatomia em um produto adaptativo. Cada
              decisão nasce do corpus oficial Einstein 2018–2026, Vunesp comparável e Enem 2023/24,
              cruzado com a identidade do curso de Psicologia.
            </p>
            <p>
              Não prometemos aprovação garantida — isso seria desonesto. Prometemos reduzir o risco
              de reprovação por meio de diagnóstico calibrado, treino adaptativo, engenharia
              reversa da banca, redação Vunesp, discursivas e simulação de desempenho.
            </p>
            <p className="text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)] border-t border-[--color-border] pt-5">
              Neuronix Study e seus produtos (PsiEinstein PatternLab e FuvestPro USP) não são
              associados oficialmente à Faculdade Israelita de Ciências da Saúde Albert Einstein,
              à Fundação Vunesp, à Fuvest ou à Universidade de São Paulo. Todas as referências às
              provas oficiais são tratadas com licenciamento ou citação curta, conforme nossa{" "}
              <Link to="/politica-de-citacao" className="link font-medium">
                política de citação
              </Link>
              .
            </p>
          </article>
        </div>

        {/* CTA */}
        <div className="mt-16 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
          <Button asChild size="xl" variant="gradient">
            <Link to="/cadastro">
              Começar diagnóstico
              <ArrowUpRight className="size-4" aria-hidden />
            </Link>
          </Button>
          <Button asChild size="xl" variant="outline">
            <Link to="/metodo">Ver método</Link>
          </Button>
        </div>
      </Container>

      {/* hide unused */}
      <Telescope className="hidden" />
    </div>
  );
}

type Tone = "violet" | "pink" | "mint" | "yellow" | "coral" | "sky";
const TONE_BADGE: Record<Tone, "accent" | "pink" | "mint" | "yellow" | "coral" | "sky"> = {
  violet: "accent",
  pink: "pink",
  mint: "mint",
  yellow: "yellow",
  coral: "coral",
  sky: "sky",
};

function PilarCard({
  tone,
  spot,
  label,
  title,
  body,
}: {
  tone: Tone;
  spot: React.ReactNode;
  label: string;
  title: string;
  body: string;
  icon?: typeof Sparkles;
}) {
  return (
    <article className="lift rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-7 shadow-[var(--shadow-sm)]">
      {spot}
      <Badge variant={TONE_BADGE[tone]} className="mt-5">
        {label}
      </Badge>
      <h3 className="mt-3 font-display text-[length:var(--text-h3)] font-semibold leading-[var(--text-h3--line-height)] tracking-[var(--text-h3--letter-spacing)] text-balance">
        {title}
      </h3>
      <p className="mt-2 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)] text-pretty">
        {body}
      </p>
    </article>
  );
}
